import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Phone, Mail, Navigation, Instagram, Facebook, Music2, Wheat, ChefHat, Timer, Asterisk } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { SITE, HOURS, isOpenNow, formatHour, MENU, AMBIANCE } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yWord = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden">
      <motion.span
        style={{ y: yWord }}
        aria-hidden="true"
        className="outline-word-dark pointer-events-none absolute -right-8 top-10 select-none font-display text-[24vw] font-black leading-none"
      >
        DELI
      </motion.span>

      <div className="mx-auto grid max-w-7xl gap-16 px-5 pb-28 pt-14 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pt-20">
        <div className="relative z-10 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-3 rounded-full border border-rust/30 bg-card px-4 py-2"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-moss" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">Fresh &amp; Authentic · Mount Druitt, Sydney</span>
          </motion.div>

          <MaskedLines
            className="mt-8 font-display text-4xl font-black leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl"
            lines={[{ text: "Cold cuts," }, { text: "carved", cls: "italic text-rust" }, { text: "with care." }]}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-smoke sm:text-lg"
          >
            Babylon Deli is a family-run neighbourhood deli on Mount Druitt Road. We stack cold meat sandwiches the
            old way — quality cuts sliced the moment you order, on your choice of focaccia, Turkish bread or
            ciabatta. Halal menu available.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/menu"
              data-testid="hero-menu-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-moss px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-moss-dark"
            >
              Explore the Menu
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#find-us"
              data-testid="hero-visit-cta"
              className="inline-flex items-center gap-3 rounded-full border border-ink/25 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-rust hover:text-rust"
            >
              Visit Our Counter
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.22em] text-smoke"
          >
            <span>Sliced to order</span>
            <Asterisk className="h-3.5 w-3.5 text-gold" />
            <span>Focaccia · Turkish · Ciabatta</span>
            <Asterisk className="h-3.5 w-3.5 text-gold" />
            <span>Family owned &amp; run</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: yImg }} className="relative mx-auto max-w-sm lg:max-w-none">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 1, ease: EASE }}
              className="arch-frame overflow-hidden border border-sand shadow-2xl"
            >
              <motion.img
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.35, duration: 1.4, ease: EASE }}
                src={MENU[0].image}
                alt={MENU[0].alt}
                className="aspect-[3/4] w-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.05, duration: 0.7, ease: EASE }}
              className="absolute -bottom-8 -left-8 grid h-32 w-32 place-items-center rounded-full bg-espresso shadow-xl"
            >
              <motion.svg
                animate={{ rotate: 360 }}
                transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
                viewBox="0 0 100 100"
                className="absolute inset-0 h-full w-full"
              >
                <defs>
                  <path id="badge-circle" d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0" />
                </defs>
                <text className="fill-cream" style={{ fontSize: "8.5px", letterSpacing: "2px" }}>
                  <textPath href="#badge-circle">BABYLON DELI · MOUNT DRUITT · SYDNEY ·</textPath>
                </text>
              </motion.svg>
              <Asterisk className="h-6 w-6 text-amber" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2, duration: 0.7, ease: EASE }}
              data-testid="hero-special-card"
              className="absolute -right-3 bottom-12 hidden rounded-xl border border-sand bg-card/95 p-4 shadow-lg backdrop-blur sm:block"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust">House Favorite</p>
              <p className="mt-1 font-display text-lg font-bold leading-tight text-ink">The Babylon Special</p>
              <p className="mt-1 font-mono text-xs text-smoke">from $16 · pick your bread</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ChapterHeading = ({ num, title, testid }) => (
  <div data-testid={testid}>
    <p className="font-mono text-xs uppercase tracking-[0.3em] text-rust sm:text-sm">{num}</p>
    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">{title}</h2>
  </div>
);

const FramedImage = ({ src, alt, className }) => (
  <div className={`relative ${className ?? ""}`}>
    <div aria-hidden="true" className="absolute inset-0 translate-x-4 translate-y-4 rounded-2xl border-2 border-gold/50" />
    <div className="group relative overflow-hidden rounded-2xl border border-sand shadow-lg">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
    </div>
  </div>
);

const CRAFT = [
  {
    icon: Wheat,
    title: "Three breads, fresh daily",
    body: "Focaccia, Turkish bread and ciabatta rolls — the base of every build, priced simple: $16–17.",
    testid: "craft-card-bread",
  },
  {
    icon: ChefHat,
    title: "Sliced to order",
    body: "Every cold cut is carved the moment you ask. Never pre-sliced, never sitting around.",
    testid: "craft-card-sliced",
  },
  {
    icon: Timer,
    title: "Halal menu",
    body: "A full halal menu — wagyu beef, halal salami, smoked turkey — built with the same care.",
    testid: "craft-card-wait",
  },
];

const BARS = [
  {
    img: "/shop/pickles-action.jpg",
    alt: "Serving olives at the self-serve pickle and olive bar at Babylon Deli",
    num: "30",
    unit: "options",
    title: "Pickle & Olive Bar",
    body: "Cornichons, full-sours, marinated olives and more — help yourself and load up your sandwich exactly how you like it.",
    testid: "pickle-olive-bar-card",
  },
  {
    img: "/shop/spice-bar.jpg",
    alt: "The self-serve spice and herb bar at Babylon Deli",
    num: "40",
    unit: "options",
    title: "Spice & Herb Bar",
    body: "Forty jars of spices, dried herbs and chilli — season your sandwich at the counter, from a pinch of oregano to a proper kick.",
    testid: "spice-herb-bar-card",
  },
];

const SelfServeBars = () => (
  <section data-testid="self-serve-section" className="double-rule mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
    <Reveal>
      <ChapterHeading num="Chapter 03" title="The Self-Serve Bars" testid="chapter-self-serve" />
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-smoke">
        No other deli in Mount Druitt does this: two help-yourself bars running along the counter. Build your
        sandwich, then finish it yourself.
      </p>
    </Reveal>

    <div className="mt-14 grid gap-10 md:grid-cols-2">
      {BARS.map((b, i) => (
        <Reveal key={b.title} delay={0.1 + i * 0.12}>
          <div data-testid={b.testid} className="group">
            <div className="relative overflow-hidden rounded-2xl border border-sand shadow-lg">
              <img
                src={b.img}
                alt={b.alt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
            <div className="mt-6 flex items-start gap-6">
              <p className="font-display text-6xl font-black leading-none text-rust sm:text-7xl">
                {b.num}
                <span className="mt-1 block font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-smoke">
                  {b.unit}
                </span>
              </p>
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-ink">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-smoke">{b.body}</p>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const FindUs = () => {
  const open = isOpenNow();
  return (
    <section id="find-us" data-testid="find-us-section" className="double-rule scroll-mt-24 bg-paper">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <Reveal>
          <ChapterHeading num="Chapter 04" title="Find Us" testid="chapter-find-us" />
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-2" delay={0.1}>
            <div data-testid="opening-hours-card" className="h-full rounded-2xl border border-sand bg-card p-8 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-display text-2xl font-bold text-ink">Opening Hours</h3>
                <span
                  data-testid="hours-status-badge"
                  className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${
                    open ? "bg-moss text-cream" : "bg-espresso text-cream"
                  }`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-cream" />
                  {open ? "Open Now" : "Closed"}
                </span>
              </div>
              <ul className="mt-7 space-y-3.5">
                {HOURS.map((h) => (
                  <li key={h.day}>
                    <div className="flex items-baseline">
                      <span className="text-sm font-semibold text-ink">{h.day}</span>
                      <span className="dot-leader" />
                      <span className="font-mono text-xs text-smoke">
                        {formatHour(h.open)} – {formatHour(h.close)}
                      </span>
                    </div>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-rust/80">
                      Deli counter until {formatHour(h.deliClose)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <div className="flex flex-col gap-10 lg:col-span-3">
            <Reveal delay={0.15}>
              <div className="grid gap-5 sm:grid-cols-3">
                <div className="flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-espresso text-cream">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <p className="text-sm leading-relaxed text-smoke">{SITE.address}</p>
                </div>
                <a href={SITE.phoneHref} data-testid="contact-phone-link" className="group flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-espresso text-cream transition-colors duration-300 group-hover:bg-moss">
                    <Phone className="h-4 w-4" />
                  </span>
                  <span className="text-sm leading-relaxed text-smoke">
                    {SITE.phone}
                    <span className="block text-xs text-smoke/70">{SITE.contact}</span>
                  </span>
                </a>
                <a href={SITE.emailHref} data-testid="contact-email-link" className="group flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-espresso text-cream transition-colors duration-300 group-hover:bg-moss">
                    <Mail className="h-4 w-4" />
                  </span>
                  <span className="break-all text-sm leading-relaxed text-smoke">{SITE.email}</span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div data-testid="map-embed" className="overflow-hidden rounded-2xl border border-sand shadow-sm">
                <iframe
                  title="Babylon Deli on Google Maps"
                  data-testid="map-iframe"
                  src={`https://maps.google.com/maps?q=${SITE.addressQuery}&z=16&output=embed`}
                  className="aspect-[16/10] w-full border-0 [filter:sepia(0.22)_saturate(0.85)]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sand bg-card px-5 py-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                    <MapPin className="h-4 w-4 shrink-0 text-rust" />
                    {SITE.address}
                  </p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${SITE.addressQuery}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="directions-link"
                    className="group inline-flex items-center gap-2 rounded-full bg-espresso px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-moss"
                  >
                    <Navigation className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
                    Get Directions
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">Follow the counter</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a
                    href={SITE.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="instagram-link"
                    className="group inline-flex items-center gap-2.5 rounded-full border border-ink/20 bg-card px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-rust hover:text-rust"
                  >
                    <Instagram className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
                    {SITE.instagramHandle}
                  </a>
                  <a
                    href={SITE.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="facebook-link"
                    className="group inline-flex items-center gap-2.5 rounded-full border border-ink/20 bg-card px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-rust hover:text-rust"
                  >
                    <Facebook className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
                    {SITE.facebookHandle}
                  </a>
                  <a
                    href={SITE.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tiktok-link"
                    className="group inline-flex items-center gap-2.5 rounded-full border border-ink/20 bg-card px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-rust hover:text-rust"
                  >
                    <Music2 className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
                    {SITE.tiktokHandle}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="border-l-4 border-rust pl-6 sm:pl-8">
              <ChapterHeading num="Chapter 01" title="Who We Are" testid="chapter-who-we-are" />
              <p className="mt-6 text-base leading-relaxed text-smoke">
                Babylon Deli started with a simple idea: a neighbourhood deserves a proper sandwich. Not something
                wrapped in plastic and shelved overnight — a sandwich built in front of you, from cold cuts carved
                that very minute.
              </p>
              <p className="mt-4 text-base leading-relaxed text-smoke">
                Today we're the local counter where Mount Druitt comes for The Babylon Special, wagyu beef and hot
                Spanish salami — run by Ray and family, seven days a week, with a full halal menu alongside the
                classics.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {["Family owned", "7 days a week", "Halal menu"].map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-sand bg-card px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-smoke"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <FramedImage src={AMBIANCE.counter} alt="Inside Babylon Deli — the counter and cold cut display" />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="order-2 lg:order-1">
            <FramedImage src={AMBIANCE.prep} alt="Preparing sandwiches at the Babylon Deli counter" />
          </Reveal>
          <Reveal className="order-1 lg:order-2" delay={0.15}>
            <div className="border-l-4 border-gold pl-6 sm:pl-8">
              <ChapterHeading num="Chapter 02" title="The Counter" testid="chapter-the-counter" />
              <p className="mt-6 text-base leading-relaxed text-smoke">
                Everything we serve passes over one well-worn counter. It's where the bread lands at sunrise, where
                the slicer hums all day, and where every sandwich gets its final wrap.
              </p>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
              {CRAFT.map((c, i) => (
                <motion.div
                  key={c.title}
                  data-testid={c.testid}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: 0.1 + i * 0.1, duration: 0.7, ease: EASE }}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl border border-sand bg-card p-5 shadow-sm"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-paper text-rust">
                    <c.icon className="h-4 w-4" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-ink">{c.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-smoke">{c.body}</p>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <SelfServeBars />

      <section data-testid="social-feed-section" className="bg-espresso text-cream">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">@babylon.deli</p>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">
                  Fresh on the feed
                </h2>
              </div>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-feed-follow-link"
                className="group inline-flex items-center gap-2.5 rounded-full border border-cream/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 hover:border-amber hover:text-amber"
              >
                <Instagram className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
                Follow along
              </a>
            </div>
          </Reveal>
          {SITE.instagramWidgetUrl ? (
            <Reveal delay={0.1}>
              <div data-testid="instagram-widget" className="mt-10 overflow-hidden rounded-2xl border border-cream/10">
                <iframe
                  title="Babylon Deli Instagram feed"
                  src={SITE.instagramWidgetUrl}
                  className="h-[720px] w-full border-0 bg-cream"
                  loading="lazy"
                  allowTransparency="true"
                />
              </div>
            </Reveal>
          ) : (
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                "/shop/pickles-action.jpg",
                "/shop/olives.jpg",
                "/shop/bread-cabinet.jpg",
                "/shop/jacket.jpg",
                "/shop/pickles.jpg",
                "/shop/counter.jpg",
              ].map((src, i) => (
                <Reveal key={src} delay={i * 0.06}>
                  <a
                    href={SITE.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`feed-photo-${i}`}
                    className="group relative block overflow-hidden rounded-xl border border-cream/10"
                  >
                    <img
                      src={src}
                      alt="Babylon Deli on Instagram"
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <span className="absolute inset-0 grid place-items-center transition-colors duration-300 group-hover:bg-espresso/40">
                      <Instagram className="h-6 w-6 text-cream opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
          )}
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
            Straight from the counter — more on Instagram, Facebook &amp; TikTok
          </p>
        </div>
      </section>

      <FindUs />
    </main>
  );
}
